import React, { useState, useEffect } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import { format, isFriday, parseISO, isSameDay } from 'date-fns';
import { toast } from "sonner";
import { Calendar } from "../../components/ui/calendar";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { fetchAvailableDays, createPlatinumBooking } from '../../api/platinumApi';
import { Loader2 } from "lucide-react";
import { cleaningApi } from '../../lib/cleaningApi';

// Google Maps libraries to load
const libraries = ['places'];

const PlacesAutocomplete = ({ onAddressSelect }) => {
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: "us" },
    },
    debounce: 300,
  });

  const handleInput = (e) => {
    setValue(e.target.value);
  };

  const handleSelect = ({ description }) => {
    setValue(description, false);
    clearSuggestions();
    onAddressSelect(description);
  };

  return (
    <div className="relative w-full">
      <Input
        value={value}
        onChange={handleInput}
        disabled={!ready}
        placeholder="Enter your address to see available dates..."
        className="w-full"
      />
      {status === "OK" && (
        <ul className="absolute z-10 w-full bg-white border border-gray-200 rounded-md mt-1 shadow-lg max-h-60 overflow-auto">
          {data.map(({ place_id, description }) => (
            <li
              key={place_id}
              onClick={() => handleSelect({ description })}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
            >
              {description}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const PlatinumBookingPage = () => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: libraries
  });

  const [address, setAddress] = useState("");
  const [availableDates, setAvailableDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [zone, setZone] = useState(null);
  const [locationDetails, setLocationDetails] = useState({ lat: null, lng: null, city: "", zip_code: "", street: "" });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [availabilityMessage, setAvailabilityMessage] = useState("");
  const platinumTimeSelectKey = `${selectedDate ? format(selectedDate, 'yyyy-MM-dd') : 'no-date'}-${loadingSlots ? 'loading' : JSON.stringify(availableSlots.map(slot => ({ time: slot.time, available: slot.available })) )}`;

  const [vehicleSize, setVehicleSize] = useState("sedan");
  
  // Form State
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    vehicleMake: "",
    vehicleModel: "",
    vehicleYear: "",
    vehicleColor: "",
    notes: ""
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddressSelect = async (selectedAddress) => {
    setAddress(selectedAddress);
    setLoadingDates(true);
    setAvailableDates([]);
    setSelectedDate(null);
    setSelectedTime("");
    setZone(null);

    try {
      // 1. Get geocode details from frontend (using restricted key)
      const results = await getGeocode({ address: selectedAddress });
      const { lat, lng } = await getLatLng(results[0]);
      
      // Extract city and zip
      let city = "";
      let zip_code = "";
      const components = results[0].address_components;
      
      components.forEach(component => {
           if (component.types.includes("locality")) {
               city = component.long_name;
           }
           if (component.types.includes("postal_code")) {
               zip_code = component.long_name;
           }
      });
      
      const addressData = {
          address: selectedAddress,
          lat,
          lng,
          city,
          zip_code
      };
      
      setLocationDetails({ lat, lng, city, zip_code, street: selectedAddress });

      // 2. Send to backend to determine zone and get dates
      const data = await fetchAvailableDays(addressData);
      setAvailableDates(data.available_dates || []);
      setZone(data.zone);
      
      if (data.available_dates && data.available_dates.length === 0) {
        toast.info("No available cleaning dates found for this address in the upcoming schedule.");
      } else {
        toast.success(`Found available dates for ${data.zone}!`);
      }
    } catch (error) {
      console.error("Failed to fetch dates", error);
      toast.error("Could not fetch available dates. Please try again.");
    } finally {
      setLoadingDates(false);
    }
  };

  // Determine if a date is disabled
  const isDateDisabled = (date) => {
    if (!availableDates.length) return true;
    
    // Check if date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return true;

    const dateStr = format(date, 'yyyy-MM-dd');
    return !availableDates.includes(dateStr);
  };

  useEffect(() => {
    if (!selectedDate || !locationDetails.city || !locationDetails.zip_code) {
      setAvailableSlots([]);
      setAvailabilityMessage("");
      return;
    }

    setLoadingSlots(true);
    setAvailabilityMessage("");

    cleaningApi
      .getAvailability(
        format(selectedDate, 'yyyy-MM-dd'),
        1,
        false,
        'residential',
        locationDetails.zip_code,
        locationDetails.street || address,
        locationDetails.city,
        locationDetails.lat,
        locationDetails.lng,
        'residential'
      )
      .then((res) => {
        const slots = res.data.slots || [];
        setAvailableSlots(slots);
        setAvailabilityMessage(res.data.message || '');

        if (selectedTime) {
          const match = slots.find((slot) => slot.time === selectedTime);
          if (!match || !match.available) {
            setSelectedTime('');
          }
        }
      })
      .catch(() => {
        setAvailableSlots([]);
        setAvailabilityMessage('Could not load available times.');
      })
      .finally(() => setLoadingSlots(false));
  }, [address, locationDetails.city, locationDetails.lat, locationDetails.lng, locationDetails.street, locationDetails.zip_code, selectedDate, selectedTime]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime || !address) {
      toast.error("Please select a date, time, and verify address.");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const bookingPayload = {
        service_type: "residential", // Platinum Package
        vehicle_size: vehicleSize,
        customer: {
          full_name: `${formData.firstName} ${formData.lastName}`,
          phone: formData.phone,
          email: formData.email
        },
        vehicle: {
          year: formData.vehicleYear,
          make: formData.vehicleMake,
          model: formData.vehicleModel,
          color: formData.vehicleColor
        },
        service_address: {
          street: address,
          city: locationDetails.city || "Lakewood",
          state: "NJ",
          zip_code: locationDetails.zip_code || "08701",
          lat: locationDetails.lat,
          lng: locationDetails.lng
        },
        preferred_date: format(selectedDate, 'yyyy-MM-dd'),
        preferred_time: selectedTime,
        notes: formData.notes
      };

      await createPlatinumBooking(bookingPayload);
      toast.success("Booking request submitted successfully!");
      // Reset form or redirect
      setAddress("");
      setAvailableDates([]);
      setSelectedDate(null);
      setFormData({
        firstName: "", lastName: "", email: "", phone: "",
        vehicleMake: "", vehicleModel: "", vehicleYear: "", vehicleColor: "", notes: ""
      });
    } catch (error) {
      console.error("Booking error", error);
      toast.error("Failed to create booking. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isLoaded) return <div className="flex justify-center items-center h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">Platinum Zone Booking</CardTitle>
          <CardDescription className="text-center">
            Enter your address to see available cleaning dates in your zone.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Address Selection */}
            <div className="space-y-2">
              <Label>Service Address</Label>
              <PlacesAutocomplete onAddressSelect={handleAddressSelect} />
              {loadingDates && <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin"/> Checking schedule...</p>}
            </div>

            {/* Calendar & Time Selection - Only show if dates available */}
            {availableDates.length > 0 && (
              <div className="grid md:grid-cols-2 gap-8 pt-4">
                <div className="space-y-2">
                  <Label>Select Date</Label>
                  <div className="border rounded-md p-4 flex justify-center">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={isDateDisabled}
                      initialFocus
                      className="rounded-md border shadow"
                    />
                  </div>
                  {zone && <p className="text-xs text-muted-foreground text-center mt-2">Zone: {zone}</p>}
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Select Time</Label>
                    <Select key={platinumTimeSelectKey} value={selectedTime} onValueChange={setSelectedTime} disabled={!selectedDate || loadingSlots}>
                      <SelectTrigger data-testid="platinum-time-select" disabled={!selectedDate || loadingSlots}>
                        <SelectValue placeholder="Choose a time slot" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableSlots.map((slot) => (
                          <SelectItem
                            key={slot.time}
                            value={slot.time}
                            disabled={!slot.available}
                            className={!slot.available ? 'bg-slate-100 text-slate-400 line-through opacity-100' : ''}
                            data-testid={`platinum-time-option-${slot.time.replace(/[: ]/g, '-')}`}
                          >
                            {slot.time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {loadingSlots && <p className="text-xs text-slate-400">Loading available times...</p>}
                    {!loadingSlots && availabilityMessage && (
                      <p className="text-xs text-slate-500" data-testid="platinum-availability-message">{availabilityMessage}</p>
                    )}
                    {selectedDate && isFriday(selectedDate) && (
                      <p className="text-xs text-amber-600">Friday bookings are limited to before 4:00 PM.</p>
                    )}
                  </div>

                  {/* Customer Details Form */}
                  <div className="pt-4 space-y-4 border-t">
                    <h3 className="font-semibold">Your Details</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="firstName">First Name</Label>
                            <Input id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} required />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="lastName">Last Name</Label>
                            <Input id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} required />
                        </div>
                    </div>
                    
                    <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required />
                    </div>
                    
                    <div className="space-y-2">
                        <Label htmlFor="phone">Phone</Label>
                        <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} required />
                    </div>

                    <h3 className="font-semibold pt-2">Vehicle Details</h3>
                    <div className="space-y-2 mb-4">
                        <Label>Vehicle Size</Label>
                        <Select value={vehicleSize} onValueChange={setVehicleSize}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select vehicle size" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="sedan">Sedan</SelectItem>
                                <SelectItem value="2_row_suv">2 Row SUV</SelectItem>
                                <SelectItem value="3_row_suv">3 Row SUV</SelectItem>
                                <SelectItem value="minivan">Minivan</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="vehicleYear">Year</Label>
                            <Input id="vehicleYear" name="vehicleYear" value={formData.vehicleYear} onChange={handleInputChange} required />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="vehicleMake">Make</Label>
                            <Input id="vehicleMake" name="vehicleMake" value={formData.vehicleMake} onChange={handleInputChange} required />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="vehicleModel">Model</Label>
                            <Input id="vehicleModel" name="vehicleModel" value={formData.vehicleModel} onChange={handleInputChange} required />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="vehicleColor">Color</Label>
                            <Input id="vehicleColor" name="vehicleColor" value={formData.vehicleColor} onChange={handleInputChange} required />
                        </div>
                    </div>

                    <Button 
                        className="w-full mt-6" 
                        onClick={handleSubmit} 
                        disabled={isSubmitting || !selectedDate || !selectedTime || !formData.email}
                    >
                        {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        {isSubmitting ? "Booking..." : "Confirm Booking"}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlatinumBookingPage;
